import {
  CampaignPhaseInput,
  getCampaignPhase,
  isAcceptingApplications,
} from "@repo/components/src/utils/campaignPhase";
import type { FreeTrialFragmentFragment } from "@repo/graphql/documents/workspace/fragment.generated";
import { FreeTrialStatus } from "@repo/graphql/gql/graphql";

const HOUR_MS = 60 * 60 * 1000;

/** A trial campaign counts as "not generating interest" after this long... */
export const LOW_INTEREST_AFTER_HOURS = 24;
/** ...with fewer applications than this. */
const LOW_INTEREST_APPLICATIONS_THRESHOLD = 20;

export type TrialPhase = "active" | "extended" | "expired";

/** The trial banner/card copy depends on three phases: running, extended by the
 *  first sent work order, and expired. The backend only flips `status` to expired
 *  on its periodic sweep, so a trial past its end date already counts as expired
 *  here. */
export const getTrialPhase = (
  trial: Pick<FreeTrialFragmentFragment, "status" | "extended" | "endsAt">,
  now: Date,
): TrialPhase => {
  const pastEnd =
    trial.endsAt != null && new Date(trial.endsAt).getTime() <= now.getTime();
  if (trial.status === FreeTrialStatus.Expired || pastEnd) return "expired";
  return trial.extended ? "extended" : "active";
};

/** Applications are closed once the campaign finished or its window ended
 *  (`endsAt` passed; `endsAt` itself still counts as open). A campaign that has
 *  not started yet is not closed, it is not open yet. */
export const isApplicationsClosed = (
  campaign: CampaignPhaseInput,
  now: Date,
): boolean => {
  const phase = getCampaignPhase(campaign, now);
  return phase === "closed" || phase === "finished";
};

/** How long a campaign's application window lasts (`startsAt` to `endsAt`), in
 *  whole hours (a window that is a few seconds off still reads as its nominal
 *  72); null without a window. */
export const applicationsWindowHours = (
  startsAt: string | null,
  endsAt: string | null,
): number | null => {
  if (startsAt == null || endsAt == null) return null;
  const windowMs = new Date(endsAt).getTime() - new Date(startsAt).getTime();
  return windowMs > 0 ? Math.round(windowMs / HOUR_MS) : null;
};

/** Published for at least 24 h, under 20 applications and still taking them.
 *  Every public campaign has an `endsAt`, so callers must only ask for trial
 *  campaigns. */
export const isLowInterestCampaign = (
  campaign: CampaignPhaseInput & {
    createdAt: string | null;
    totalPostulations: number;
  },
  now: Date,
): boolean => {
  if (campaign.createdAt == null || campaign.endsAt == null) {
    return false;
  }
  const publishedForMs = now.getTime() - new Date(campaign.createdAt).getTime();
  return (
    publishedForMs >= LOW_INTEREST_AFTER_HOURS * HOUR_MS &&
    campaign.totalPostulations < LOW_INTEREST_APPLICATIONS_THRESHOLD &&
    isAcceptingApplications(campaign, now)
  );
};

export const hasReachedLimit = (used: number, limit: number | null): boolean =>
  limit != null && used >= limit;

/** "2/5", or just "2" when the plan has no limit. */
export const formatUsage = (used: number, limit: number | null): string =>
  limit == null ? `${used}` : `${used}/${limit}`;
